import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Row, Col, Button, Card, Spinner, InputGroup } from "react-bootstrap";
import { FaTrash, FaPlus, FaImage, FaFile, FaDownload } from "react-icons/fa";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";

const AddSchool = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);

  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  const [loading, setLoading] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);

  const [grades, setGrades] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    registration_number: "",
    school_code: "",
    school_name: "",
    password: "",
    email: "",
    mobile: "",
    country_code: "233",
    address: "",
    school_type: "",
    no_of_student: "",
    grades: [],
    contact_persons: [{ name: "", designation: "", email: "", phone: "" }],
    fax: "",
    city: "",
    state: "",
    country: "233",
    zip_code: "",
    website: "",
    social_link: "",
    established_year: "",
    no_of_teacher: "",
    logo: null,
    documents: null
  });

  const [errors, setErrors] = useState({});
  const [mobileCharLimit, setMobileCharLimit] = useState(14);
  const [existingFiles, setExistingFiles] = useState({
    logo: null,
    documents: null
  });
  const [logoPreview, setLogoPreview] = useState(null);

  // Character limit constants
  const CHAR_LIMITS = {
    school_name: 100,
    school_code: 20,
    registration_number: 50,
    email: 100,
    mobile: 14,
    address: 200,
    website: 200,
    social_link: 500,
    fax: 20,
    zip_code: 10,
    contact_person_name: 50,
    contact_person_designation: 50,
    contact_person_email: 100,
    contact_person_phone: 14,
    no_of_student: 10,  
    no_of_teacher: 6,
  };

  // Fetch Grades on component mount
  useEffect(() => {
    fetchGrades();
    fetchCountries();
  }, []);

  // If editing, fetch school details
  useEffect(() => {
    if (id) {
      fetchSchoolDetails();
    }
  }, [id]);

  // Fetch states when country changes (for edit mode)
  useEffect(() => {
    if (formData.country) {
      fetchStates(formData.country);
    }
  }, [formData.country]);

  // Fetch cities when state changes (for edit mode)
  useEffect(() => {
    if (formData.state) {
      fetchCities(formData.state);
    }
  }, [formData.state]);


  useEffect(() => {
    if (formData.country_code && countries.length > 0) {
      const country = countries.find(c => String(c.id) === String(formData.country_code));
      if (country?.mnl) {
        const mnl = String(country.mnl);
        if (mnl.includes('-')) {
          setMobileCharLimit(parseInt(mnl.split('-').pop(), 10) || 14);
        } else {
          setMobileCharLimit(parseInt(mnl, 10) || 14);
        }
      } else {
        setMobileCharLimit(14);
      }
    }
  }, [formData.country_code, countries]);

  const fetchSchoolDetails = async () => {
    setFetchingDetails(true);
    try {
      const response = await APIServices.get(
        `/admin/school/details/${id}`,
        headers
      );

      if (response?.data?.status === true) {
        const schoolData = response?.data?.data;

        // Parse grade_ids string to array
        const gradeIdsArray = schoolData.grade_ids ? schoolData.grade_ids.split(',').map(String) : [];

        // Parse contact persons if exists
        let contactPersons = [{ name: "", designation: "", email: "", phone: "" }];
        if (schoolData.contact_persons && schoolData.contact_persons.length > 0) {
          contactPersons = schoolData.contact_persons;
        }

        // Set form data
        setFormData({
          registration_number: schoolData.registration_number || "",
          school_code: schoolData.school_code || "",
          school_name: schoolData.school_name || "",
          password: "",
          email: schoolData.email || "",
          mobile: schoolData.mobile || "",
          country_code: schoolData.country_code || "+1",
          address: schoolData.address || "",
          school_type: schoolData.school_type || "",
          no_of_student: schoolData.no_of_student || "",
          grades: gradeIdsArray,
          contact_persons: contactPersons,
          fax: schoolData.fax || "",
          city: schoolData.city_id || "",
          state: schoolData.state_id || "",
          country: schoolData.country_id || "233",
          zip_code: schoolData.zip_code || "",
          website: schoolData.website || "",
          social_link: schoolData.social_link || "",
          established_year: schoolData.established_year || "",
          no_of_teacher: schoolData.no_of_teacher || "",
          logo: null,
          documents: null
        });

        setExistingFiles({
          logo: schoolData.logo || null,
          documents: schoolData.documents || null
        });

        // Fetch states for the country
        if (schoolData.country_id) {
          await fetchStates(schoolData.country_id);
        }

        // Fetch cities for the state
        if (schoolData.state_id) {
          await fetchCities(schoolData.state_id);
        }
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
      showToast("Error fetching school details", "error");
    } finally {
      setFetchingDetails(false);
    }
  };

  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/grade_list?status=Active&limit=100`,
        headers
      );

      if (response?.data?.status === true) {
        const gradesData = response?.data?.data?.grades || response?.data?.data?.data || [];
        setGrades(gradesData);
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
      showToast("Error fetching grades", "error");
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/country_list`,
        headers
      );

      if (response?.data?.status === true) {
        setCountries(response?.data?.data?.countries || []);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      showToast("Error fetching countries", "error");
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchStates = async (countryId) => {
    setLoadingStates(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/state_list?country_id=${countryId}`,
        headers
      );

      if (response?.data?.status === true) {
        setStates(response?.data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      showToast("Error fetching states", "error");
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    setLoadingCities(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/city_list?state_id=${stateId}`,
        headers
      );

      if (response?.data?.status === true) {
        setCities(response?.data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      showToast("Error fetching cities", "error");
    } finally {
      setLoadingCities(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      const file = files[0];
      setFormData({ ...formData, [name]: file });

      // Generate preview for logo
      if (name === 'logo' && file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (name === 'logo') {
        setLogoPreview(null);
      }

      // Clear existing file when new file is selected
      if (name === 'logo') {
        setExistingFiles(prev => ({ ...prev, logo: null }));
      } else if (name === 'documents') {
        setExistingFiles(prev => ({ ...prev, documents: null }));
      }
    } else if (name === 'grades') {
      let updatedGrades = [...formData.grades];
      if (checked) {
        updatedGrades.push(value);
      } else {
        updatedGrades = updatedGrades.filter(id => id !== value);
      }
      setFormData({ ...formData, grades: updatedGrades });
    } else {
      // Apply character limits
      let limitedValue = value;
      if (name === 'mobile') {
        limitedValue = value.slice(0, mobileCharLimit);
      } else if (CHAR_LIMITS[name]) {
        limitedValue = value.slice(0, CHAR_LIMITS[name]);
      }
      setFormData({ ...formData, [name]: limitedValue });
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleContactPersonChange = (index, field, value) => {
    // Apply character limits for contact person fields
    let limitedValue = value;
    const limitKey = `contact_person_${field}`;
    if (CHAR_LIMITS[limitKey]) {
      limitedValue = value.slice(0, CHAR_LIMITS[limitKey]);
    }

    const updatedContacts = [...formData.contact_persons];
    updatedContacts[index][field] = limitedValue;
    setFormData({ ...formData, contact_persons: updatedContacts });

    // CLEAR CONTACT PERSON ERROR
    const hasValidContact = updatedContacts.some(
      (person) =>
        person.name?.trim() &&
        person.designation?.trim() &&
        person.email?.trim() &&
        person.phone?.trim()
    );

    // REMOVE ERROR WHEN VALID
    if (hasValidContact) {
      setErrors((prev) => ({
        ...prev,
        contact_persons: ""
      }));
    }

  };

  const addContactPerson = () => {
    setFormData({
      ...formData,
      contact_persons: [
        ...formData.contact_persons,
        { name: "", designation: "", email: "", phone: "" }
      ]
    });
  };

  const removeContactPerson = (index) => {
    if (formData.contact_persons.length === 1) {
      showToast("At least one contact person is required", "error");
      return;
    }
    const updatedContacts = formData.contact_persons.filter((_, i) => i !== index);
    setFormData({ ...formData, contact_persons: updatedContacts });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.registration_number?.trim()) {
      newErrors.registration_number = "Registration number is required";
    } else if (formData.registration_number.length > CHAR_LIMITS.registration_number) {
      newErrors.registration_number = `Registration number cannot exceed ${CHAR_LIMITS.registration_number} characters`;
    }

    if (!formData.school_code?.trim()) {
      newErrors.school_code = "School code is required";
    } else if (formData.school_code.length > CHAR_LIMITS.school_code) {
      newErrors.school_code = `School code cannot exceed ${CHAR_LIMITS.school_code} characters`;
    }

    if (!formData.school_name?.trim()) {
      newErrors.school_name = "School name is required";
    } else if (formData.school_name.length > CHAR_LIMITS.school_name) {
      newErrors.school_name = `School name cannot exceed ${CHAR_LIMITS.school_name} characters`;
    }

    if (!id && !formData.password) {
      newErrors.password = "Password is required";
    } else if (!id && formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    } else if (formData.email.length > CHAR_LIMITS.email) {
      newErrors.email = `Email cannot exceed ${CHAR_LIMITS.email} characters`;
    }

    if (!formData.mobile?.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d+$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must contain only digits";
    } else if (formData.mobile.length > mobileCharLimit) {
      newErrors.mobile = `Mobile number cannot exceed ${mobileCharLimit} digits`;
    }

    if (!formData.address?.trim()) {
      newErrors.address = "Address is required";
    } else if (formData.address.length > CHAR_LIMITS.address) {
      newErrors.address = `Address cannot exceed ${CHAR_LIMITS.address} characters`;
    }

    if (!formData.no_of_student) {
      newErrors.no_of_student = "Number of students is required";
    }

    if (!formData.no_of_teacher) {
      newErrors.no_of_teacher = "Number of teachers is required";
    }

    if (!formData.school_type) {
      newErrors.school_type = "School type is required";
    }
    if (formData.grades.length === 0) {
      newErrors.grades = "At least one grade is required";
    }
    if (!formData.country) {
      newErrors.country = "Country is required";
    }
    if (!formData.state) {
      newErrors.state = "State is required";
    }
    if (!formData.city) {
      newErrors.city = "City is required";
    }

 const hasValidContact = formData.contact_persons.some(person =>
      person.name?.trim() && person.designation?.trim() && person.email?.trim() &&person.phone?.trim()
    );
    if (!hasValidContact) {
      newErrors.contact_persons = "At least one contact person with valid information is required";
    }

    formData.contact_persons.forEach((person, index) => {
      if (person.name && person.name.length > CHAR_LIMITS.contact_person_name) {
        newErrors[`contact_person_${index}_name`] = `Name cannot exceed ${CHAR_LIMITS.contact_person_name} characters`;
      }
      if (person.designation && person.designation.length > CHAR_LIMITS.contact_person_designation) {
        newErrors[`contact_person_${index}_designation`] = `Designation cannot exceed ${CHAR_LIMITS.contact_person_designation} characters`;
      }
      if (person.email && person.email.length > CHAR_LIMITS.contact_person_email) {
        newErrors[`contact_person_${index}_email`] = `Email cannot exceed ${CHAR_LIMITS.contact_person_email} characters`;
      }
      if (person.phone && person.phone.length > CHAR_LIMITS.contact_person_phone) {
        newErrors[`contact_person_${index}_phone`] = `Phone number cannot exceed ${CHAR_LIMITS.contact_person_phone} digits`;
      }
      if (person.email && !/\S+@\S+\.\S+/.test(person.email)) {
        newErrors[`contact_person_${index}_email`] = "Email is invalid";
      }
      if (person.phone && !/^\d*$/.test(person.phone)) {
        newErrors[`contact_person_${index}_phone`] = "Phone must contain only digits";
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showToast("Please fill all required fields correctly", "error");
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("registration_number", formData.registration_number);
      formDataToSend.append("school_code", formData.school_code);
      formDataToSend.append("school_name", formData.school_name);

      if (!id || (id && formData.password)) {
        formDataToSend.append("password", formData.password);
      }

      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile", formData.mobile);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("school_type", formData.school_type);
      formDataToSend.append("grade", formData.grades.join(","));

      if (formData.no_of_student) formDataToSend.append("no_of_student", formData.no_of_student);
      if (formData.fax) formDataToSend.append("fax", formData.fax);
      if (formData.city) formDataToSend.append("city", formData.city);
      if (formData.state) formDataToSend.append("state", formData.state);
      if (formData.country) formDataToSend.append("country", formData.country);
      if (formData.zip_code) formDataToSend.append("zip_code", formData.zip_code);
      if (formData.website) formDataToSend.append("website", formData.website);
      if (formData.social_link) formDataToSend.append("social_link", formData.social_link);
      if (formData.established_year) formDataToSend.append("established_year", formData.established_year);
      if (formData.no_of_teacher) formDataToSend.append("no_of_teacher", formData.no_of_teacher);
      if (formData.country_code) formDataToSend.append("country_code", formData.country_code);

      if (formData.contact_persons.length > 0) {
        formDataToSend.append("contact_persons", JSON.stringify(formData.contact_persons));
      }

      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }
      if (formData.documents) {
        formDataToSend.append("documents", formData.documents);
      }

      let response;

      if (id) {
        formDataToSend.append("id", id);
        response = await APIServices.put(
          "/admin/school/update",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      } else {
        response = await APIServices.post(
          "/admin/school/add",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      }

      if (response?.data?.status === true) {
        showToast(response?.data?.message || (id ? "School updated successfully" : "School added successfully"), "success");
        setTimeout(() => {
          navigate("/admin/school");
        }, 2000);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Error saving school:", error);
      showToast(error.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  if (fetchingDetails) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading school details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="add_school_sec">
        <div className="addmom_header d-flex justify-content-between align-items-center mb-3">
          <h1 className="font_blue_18 font-600">
            {id ? "Edit School" : "Add School"}
          </h1>
        </div>

        <Card className="add_school_form">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <h5 className="mb-3 text-primary">Basic Information</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>School Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="school_name"
                      placeholder="Enter school name"
                      value={formData.school_name}
                      onChange={handleChange}
                      isInvalid={!!errors.school_name}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.school_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.school_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>School Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="school_code"
                      placeholder="Enter school code"
                      value={formData.school_code}
                      onChange={handleChange}
                      isInvalid={!!errors.school_code}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.school_code}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.school_code}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Registration Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="registration_number"
                      placeholder="Enter registration number"
                      value={formData.registration_number}
                      onChange={handleChange}
                      isInvalid={!!errors.registration_number}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.registration_number}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.registration_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {!id && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="Enter password (min 6 characters)"
                        value={formData.password}
                        onChange={handleChange}
                        isInvalid={!!errors.password}
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font_blue_14 font-500 mb-2">Mobile <span className="text-danger">*</span></Form.Label>
                    <InputGroup>
                      <Form.Select
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleChange}
                        style={{ maxWidth: "180px", borderRight: "2px solid #D5D4DD" }}
                        disabled={loading}
                      >
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name} ({country.phonecode})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        name="mobile"
                        placeholder="Enter mobile number"
                        value={formData.mobile}
                        onChange={handleChange}
                        isInvalid={!!errors.mobile}
                        disabled={loading}
                        maxLength={mobileCharLimit}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.mobile}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Location Section */}
              <h5 className="mb-3 text-primary mt-3">Location Information</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      isInvalid={!!errors.country}
                      disabled={loadingCountries || loading}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingCountries && <Spinner animation="border" size="sm" />}
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>State <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      isInvalid={!!errors.state}
                      disabled={!formData.country || loadingStates || loading}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingStates && <Spinner animation="border" size="sm" />}
                    <Form.Control.Feedback type="invalid">
                      {errors.state}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>City <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      isInvalid={!!errors.city}
                      disabled={!formData.state || loadingCities || loading}
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingCities && <Spinner animation="border" size="sm" />}
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="address"
                      placeholder="Enter address"
                      value={formData.address}
                      onChange={handleChange}
                      isInvalid={!!errors.address}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Zip Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="zip_code"
                      placeholder="Enter zip code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.zip_code}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* School Details Section */}
              <h5 className="mb-3 text-primary mt-3">School Details</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>School Type <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="school_type"
                      value={formData.school_type}
                      onChange={handleChange}
                      isInvalid={!!errors.school_type}
                      disabled={loading}
                    >
                      <option value="">Select Type</option>
                      <option value="Private">Private</option>
                      <option value="Public">Public</option>
                      <option value="Government">Government</option>
                      <option value="International">International</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.school_type}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Students <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="no_of_student"
                      placeholder="Enter number of students"
                      value={formData.no_of_student}
                      onChange={handleChange}
                      isInvalid={!!errors.no_of_student}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.no_of_student}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.no_of_student}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Teachers <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="no_of_teacher"
                      placeholder="Enter number of teachers"
                      value={formData.no_of_teacher}
                      onChange={handleChange}
                      isInvalid={!!errors.no_of_teacher}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.no_of_teacher}

                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.no_of_teacher}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Established Year</Form.Label>
                    <Form.Control
                      type="text"
                      name="established_year"
                      placeholder="YYYY"
                      value={formData.established_year}
                      maxLength={4}
                      pattern="\d{4}"
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 4) {
                          setFormData({
                            ...formData,
                            established_year: value,
                          });
                        }
                      }}
                      disabled={loading}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                      type="text"
                      name="website"
                      placeholder="Enter website URL"
                      value={formData.website}
                      onChange={handleChange}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.website}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fax</Form.Label>
                    <Form.Control
                      type="text"
                      name="fax"
                      placeholder="Enter fax number"
                      value={formData.fax}
                      onChange={handleChange}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.fax}
                      onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, "") }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Social Links (comma separated)</Form.Label>
                    <Form.Control
                      type="text"
                      name="social_link"
                      placeholder="https://facebook.com/school, https://twitter.com/school"
                      value={formData.social_link}
                      onChange={handleChange}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.social_link}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Grades Section */}
              <h5 className="mb-3 text-primary mt-3">
                Grades <span className="text-danger">*</span>
              </h5>
              {errors.grades && <div className="text-danger mb-2">{errors.grades}</div>}

              <div className="grade-checkbox">
                <Form.Check
                  type="checkbox"
                  id="grade-all"
                  label="All Grades"
                  checked={formData.grades.length === grades.length && grades.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allGradeIds = grades.map(grade => String(grade.id));
                      setFormData({ ...formData, grades: allGradeIds });
                      setErrors((prev) => ({
                        ...prev,
                        grades: ""
                      }));
                    } else {
                      setFormData({ ...formData, grades: [] });
                    }
                  }}
                  disabled={loadingGrades || loading}
                />
              </div>
              <hr />
              <div className="row">
                {loadingGrades ? (
                  <div className="col-12 text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  grades.map((grade) => (
                    <div className="col-md-2 mb-2" key={grade.id}>
                      <div className="grade-checkbox">
                        <Form.Check
                          type="checkbox"
                          id={`grade-${grade.id}`}
                          label={grade.name}
                          checked={formData.grades.includes(String(grade.id))}
                          onChange={(e) => {
                            let updatedGrades = [...formData.grades];
                            if (e.target.checked) {
                              updatedGrades.push(String(grade.id));
                            } else {
                              updatedGrades = updatedGrades.filter(id => id !== String(grade.id));
                            }
                            setFormData({ ...formData, grades: updatedGrades });

                              // CLEAR ERROR WHEN AT LEAST ONE GRADE EXISTS
                              if (updatedGrades.length > 0) {
                                setErrors((prev) => ({
                                  ...prev,
                                  grades: ""
                                }));
                              }
                          }}
                          disabled={loadingGrades || loading}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Contact Persons Section */}
              <h5 className="mb-3 text-primary mt-3">Contact Persons <span className="text-danger">*</span></h5>
              {errors.contact_persons && <div className="text-danger mb-2">{errors.contact_persons}</div>}

              {formData.contact_persons.map((person, index) => (
                <Card key={index} className="mb-3 p-3">
                  <Row>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter name"
                          value={person.name}
                          onChange={(e) => handleContactPersonChange(index, "name", e.target.value)}
                          disabled={loading}
                          maxLength={CHAR_LIMITS.contact_person_name}
                          isInvalid={!!errors[`contact_person_${index}_name`]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[`contact_person_${index}_name`]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Designation</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter designation"
                          value={person.designation}
                          onChange={(e) => handleContactPersonChange(index, "designation", e.target.value)}
                          disabled={loading}
                          maxLength={CHAR_LIMITS.contact_person_designation}
                          isInvalid={!!errors[`contact_person_${index}_designation`]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[`contact_person_${index}_designation`]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter email"
                          value={person.email}
                          onChange={(e) => handleContactPersonChange(index, "email", e.target.value)}
                          disabled={loading}
                          maxLength={CHAR_LIMITS.contact_person_email}
                          isInvalid={!!errors[`contact_person_${index}_email`]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[`contact_person_${index}_email`]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter phone"
                          value={person.phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            handleContactPersonChange(index, "phone", value);
                          }}
                          disabled={loading}
                          maxLength={CHAR_LIMITS.contact_person_phone}
                          isInvalid={!!errors[`contact_person_${index}_phone`]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[`contact_person_${index}_phone`]}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end mt-2">
                      {index > 0 && (
                        <Button variant="danger" onClick={() => removeContactPerson(index)} disabled={loading}>
                          <FaTrash />
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card>
              ))}
              <Button variant="outline-primary" onClick={addContactPerson} className="mb-3" disabled={loading}>
                <FaPlus className="me-1" /> Add Contact Person
              </Button>

              {/* File Uploads Section */}
              <h5 className="mb-3 text-primary mt-3">Document</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>School Logo</Form.Label>
                    <div className="upload-file-box">
                      <Form.Control
                        type="file"
                        name="logo"
                        accept="image/*"
                        onChange={handleChange}
                        disabled={loading}
                        className="upload-input"
                      />
                      {logoPreview ? (
                        <div className="file-preview-container">
                          <img
                            src={logoPreview}
                            alt="New Logo Preview"
                            className="img-preview"
                          />
                          <span className="remove-file" onClick={() => {
                            setFormData({ ...formData, logo: null });
                            setLogoPreview(null);
                          }}>×</span>
                        </div>
                      ) : formData.logo ? (
                        <div className="file-selected">
                          <FaImage className="me-2" />
                          <span className="file-name">{formData.logo.name}</span>
                        </div>
                      ) : existingFiles.logo ? (
                        <div className="existing-file-preview">
                          <img
                            src={existingFiles.logo}
                            alt="Current Logo"
                          />
                          <span className="remove-file" onClick={() => {
                            setFormData({ ...formData, logo: null });
                            setExistingFiles({ ...existingFiles, logo: null });
                          }}>×</span>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <FaImage />
                          <span>Choose logo image</span>
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Document (Registration, Affiliation, etc.)</Form.Label>
                    <div className="upload-file-box">
                      <Form.Control
                        type="file"
                        name="documents"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleChange}
                        disabled={loading}
                        className="upload-input"
                      />
                      {formData.documents ? (
                        <div className="file-selected">
                          <FaFile className="me-2" />
                          <span className="file-name">{formData.documents.name}</span>
                        </div>
                      ) : existingFiles.documents ? (
                        <div className="existing-file-preview">
                          <FaFile className="file-icon" />
                          <span className="file-label">Document uploaded</span>
                          <span className="remove-file" onClick={() => {
                            setFormData({ ...formData, documents: null });
                            setExistingFiles({ ...existingFiles, documents: null });
                          }}>×</span>
                        </div>
                      ) : (
                        <div className="upload-placeholder">
                          <FaFile />
                          <span>Choose document file</span>
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              {/* Submit Buttons */}
              <div className="text-center mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="me-2"
                >
                  {loading ? "Processing..." : (id ? "Update" : "Add")}
                </Button>
                <Button variant="secondary" onClick={() => navigate("/admin/school")}>
                  Back
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <ToastContainer />
    </>
  );
};

export default AddSchool;